import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Card } from 'react-bootstrap';
import receptionService from '../../../services/receptionService';
import { ReceptionLoadingBlock, ReceptionEmptyState } from '../ui';
import OrientedConsultationsListRows from './OrientedConsultationsListRows';

const CONSULT_PAGE_SIZE = 10;

export default function ReceptionOrientedConsultationsPanel({
  toast,
  reloadSignal = 0,
  activeReceptionSite = null
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const activeSiteId = activeReceptionSite?.id != null ? String(activeReceptionSite.id) : '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await receptionService.getOrientedConsultationsToday();
      if (res.success) setRows(Array.isArray(res.data) ? res.data : []);
      else toast('warning', res.message || 'Données indisponibles');
    } catch (e) {
      toast('danger', e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load, reloadSignal]);

  useEffect(() => {
    setPage(0);
  }, [activeSiteId, reloadSignal]);

  const visibleRows = useMemo(() => {
    if (!activeSiteId) return rows;
    return rows.filter((row) => String(row.site_id || '') === activeSiteId);
  }, [activeSiteId, rows]);

  const totalCount = visibleRows.length;
  const pageCount = useMemo(() => {
    if (!totalCount) return 0;
    return Math.max(1, Math.ceil(totalCount / CONSULT_PAGE_SIZE));
  }, [totalCount]);

  useEffect(() => {
    if (pageCount > 0 && page >= pageCount) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const rangeLabel = useMemo(() => {
    if (!totalCount) return null;
    const from = page * CONSULT_PAGE_SIZE + 1;
    const to = Math.min(totalCount, (page + 1) * CONSULT_PAGE_SIZE);
    return `${from}–${to}`;
  }, [page, totalCount]);

  const paginatedRows = useMemo(() => {
    const start = page * CONSULT_PAGE_SIZE;
    return visibleRows.slice(start, start + CONSULT_PAGE_SIZE);
  }, [visibleRows, page]);

  const countLabel = loading
    ? 'Chargement…'
    : `${totalCount} dossier${totalCount > 1 ? 's' : ''}${
        pageCount > 1 ? ` · ${CONSULT_PAGE_SIZE} par page` : ''
      }`;

  const listHead = (
    <div className="recv-dash-consult-list__head" aria-hidden="true">
      <span className="recv-dash-consult-list__cell recv-dash-consult-list__cell--patient">Patient</span>
      <span className="recv-dash-consult-list__cell recv-dash-consult-list__cell--care">Soins</span>
      <span className="recv-dash-consult-list__cell recv-dash-consult-list__cell--dossier">Dossier</span>
    </div>
  );

  const renderPagination = (position = 'bottom') => {
    if (loading || totalCount <= 0) return null;
    const multiPage = pageCount > 1;
    return (
      <div
        className={`recv-journal-pagination recv-browse-pagination${
          position === 'top' ? ' mb-3 mt-3' : ' mt-0 border-top'
        }`}
        aria-label="Pagination des consultations orientées"
      >
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page <= 0 || loading}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Précédent
        </Button>
        <div className="recv-journal-pagination__info">
          Page <strong>{page + 1}</strong> / {pageCount}
          {' · '}
          <strong>{totalCount}</strong> dossier{totalCount > 1 ? 's' : ''}
          {rangeLabel ? (
            <>
              {' '}
              (lignes {rangeLabel})
            </>
          ) : null}
          {!multiPage ? (
            <span className="d-block small mt-1">Tous les résultats sont sur cette page.</span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={page >= pageCount - 1 || loading}
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
        >
          Suivant →
        </Button>
      </div>
    );
  };

  return (
    <Card className="border-0 recv-dash-card recv-dash-card--elevated recv-panel-card recv-panel-card--dash-consult">
      <Card.Header className="recv-browse-panel__header border-bottom-0 pb-0">
        <div className="recv-browse-panel__hero recv-browse-panel__hero--compact">
          <div className="recv-browse-panel__intro">
            <div className="recv-browse-panel__eyebrow">Dossiers du jour</div>
            <div className="recv-browse-panel__title">Consultations orientées</div>
            <p className="recv-browse-panel__desc mb-0">
              {activeReceptionSite?.name ? `${activeReceptionSite.name} · ` : ''}
              {countLabel}.
            </p>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="recv-browse-panel__body pt-0 pb-0">
        {loading ? (
          <ReceptionLoadingBlock label="Chargement des consultations…" />
        ) : visibleRows.length === 0 ? (
          <ReceptionEmptyState
            title="Aucun dossier pour ce site"
            description={`Les consultations orientées depuis ${activeReceptionSite?.name || 'ce site'} apparaîtront ici.`}
          />
        ) : (
          <>
            {renderPagination('top')}
            <div className="recv-dash-consult-list-wrap border rounded bg-white mt-3 mb-0">
              <div className="recv-dash-consult-list" role="list" aria-label="Consultations orientées du jour">
                {listHead}
                <OrientedConsultationsListRows rows={paginatedRows} />
              </div>
            </div>
            {renderPagination('bottom')}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
